export interface UserAction {
  id: string;
  actionTimeUTC: Date;
  ip: string;
  userId: string;
  userName: string;
  actionType: UserActionTypeEnum;
  comment: string;
  from: string;
  to: string;
  entityType: UserActionEntityTypeEnum;
  entityId: string;
  organizationId: string;
  organizationName: string;
}

export interface UserActionFilter {
  userId?: string;
  actionType?: UserActionTypeEnum;
  entityType?: UserActionEntityTypeEnum;
  entityId?: string;
  organizationId?: string;
  pageSize: number;
  pageIndex: number;
  actionTimeFrom?: string;
  actionTimeTo?: string;
}

export enum UserActionTypeEnum {
  PipeSwitching = 'PipeSwitching',
  PipeUpdate = 'PipeUpdate',
  PipeDelete = 'PipeDelete',
  ConfigurationUpdate = 'ConfigurationUpdate',
  ConfigurationDelete = 'ConfigurationDelete',
  ConfigurationRestore = 'ConfigurationRestore',
  ConfigurationRollback = 'ConfigurationRollback',
  PipeRestore = 'PipeRestore',
  UploadMenu = 'UploadMenu',
  UploadStopList = 'UploadStopList',
  CreateUpdatingSchedule = 'CreateUpdatingSchedule',
  UpgradeUpdatingSchedule = 'UpgradeUpdatingSchedule',
  RemoveUpdatingSchedule = 'RemoveUpdatingSchedule',
  ConfigurationMigrate = 'ConfigurationMigrate',
  PipeMove = 'PipeMove',
  ConfigurationMove = 'ConfigurationMove',
  OrganizationUpdate = 'OrganizationUpdate',
  ConfirmPublishingMenu = 'ConfirmPublishingMenu',
  CancelPublishingMenu = 'CancelPublishingMenu',
  DeactivateAllTokens = 'DeactivateAllTokens',
  SpecialConvertInstructionUpdate = 'SpecialConvertInstructionUpdate',
  OrganizationCreate = 'OrganizationCreate',
  PipeCreate = 'PipeCreate',
  ConfigurationCreate = 'ConfigurationCreate',
  ExternalOrganizationCreate = 'ExternalOrganizationCreate',
  ExternalOrganizationUpdate = 'ExternalOrganizationUpdate',
  ExternalOrganizationDelete = 'ExternalOrganizationDelete',
  NetworkCreate = 'NetworkCreate',
  NetworkUpdate = 'NetworkUpdate',
  NetworkMerge = 'NetworkMerge',
  TargetVersionUpdate = 'TargetVersionUpdate',
  LicenseUpdate = 'LicenseUpdate',
  KioskContentUpdate = 'KioskContentUpdate',
  KioskVersionCreate = 'KioskVersionCreate',
  KioskVersionUpdate = 'KioskVersionUpdate',
  KioskVersionDelete = 'KioskVersionDelete',
  KioskConfigCreate = 'KioskConfigCreate',
  KioskConfigUpdate = 'KioskConfigUpdate',
  KioskConfigDelete = 'KioskConfigDelete',
  CredentialsUpdate = 'CredentialsUpdate',
  WipeExecuted = 'WipeExecuted',
  ReplayExecuted = 'ReplayExecuted',
  BackfillExecuted = 'BackfillExecuted',
  UpdateRuleCreate = 'UpdateRuleCreate',
  UpdateRuleUpdate = 'UpdateRuleUpdate',
  UpdateRuleDelete = 'UpdateRuleDelete',
  UpdateRuleOverrideSet = 'UpdateRuleOverrideSet',
  UpdateRuleOverrideDelete = 'UpdateRuleOverrideDelete',
  ApplicationSettingUpdate = 'ApplicationSettingUpdate',
  ApplicationSettingReset = 'ApplicationSettingReset',
  ApplicationSettingRevert = 'ApplicationSettingRevert',
  ApplicationSettingMoveToCommon = 'ApplicationSettingMoveToCommon',
  PosFileUpload = 'PosFileUpload',
  PosFileDelete = 'PosFileDelete',
  PosReboot = 'PosReboot',
  PosRestartFront = 'PosRestartFront',
  PosRestartComputer = 'PosRestartComputer',
  PosCreate = 'PosCreate',
  PosUpdate = 'PosUpdate',
  PosDelete = 'PosDelete',
  TerminalCreate = 'TerminalCreate',
  TerminalUpdate = 'TerminalUpdate',
  TerminalDelete = 'TerminalDelete',
}

export enum UserActionEntityTypeEnum {
  Organization = 'Organization',
  Pipe = 'Pipe',
  Configuration = 'Configuration',
  Menu = 'Menu',
  StopList = 'StopList',
  UpdatingSchedule = 'UpdatingSchedule',
  MenuStorage = 'MenuStorage',
  Authorization = 'Authorization',
  SpecialConvertInstruction = 'SpecialConvertInstruction',
  ExternalOrganization = 'ExternalOrganization',
  Network = 'Network',
  TargetVersion = 'TargetVersion',
  License = 'License',
  KioskContent = 'KioskContent',
  KioskVersion = 'KioskVersion',
  KioskConfig = 'KioskConfig',
  Credentials = 'Credentials',
  KioskMaintenance = 'KioskMaintenance',
  UpdateRule = 'UpdateRule',
  ApplicationSetting = 'ApplicationSetting',
  PosFile = 'PosFile',
  Pos = 'Pos',
  Terminal = 'Terminal',
}

/**
 * Контур, для которого собран фронт (environment.publishedFor).
 */
export type PublishedFor = 'staging' | 'kiosk' | 'production';

/**
 * Типы действий и сущностей, которые бывают только в экосистеме киосков.
 * Список ведётся явно, а не «всё, что после N-го значения»: enum дописывается
 * с обеих сторон, и порядковая эвристика однажды сломается молча.
 */
export const KIOSK_USER_ACTION_TYPES: readonly UserActionTypeEnum[] = [
  UserActionTypeEnum.ExternalOrganizationCreate,
  UserActionTypeEnum.ExternalOrganizationUpdate,
  UserActionTypeEnum.ExternalOrganizationDelete,
  UserActionTypeEnum.NetworkCreate,
  UserActionTypeEnum.NetworkUpdate,
  UserActionTypeEnum.NetworkMerge,
  UserActionTypeEnum.TargetVersionUpdate,
  UserActionTypeEnum.LicenseUpdate,
  UserActionTypeEnum.KioskContentUpdate,
  UserActionTypeEnum.KioskVersionCreate,
  UserActionTypeEnum.KioskVersionUpdate,
  UserActionTypeEnum.KioskVersionDelete,
  UserActionTypeEnum.KioskConfigCreate,
  UserActionTypeEnum.KioskConfigUpdate,
  UserActionTypeEnum.KioskConfigDelete,
  UserActionTypeEnum.CredentialsUpdate,
  UserActionTypeEnum.WipeExecuted,
  UserActionTypeEnum.ReplayExecuted,
  UserActionTypeEnum.BackfillExecuted,
  UserActionTypeEnum.UpdateRuleCreate,
  UserActionTypeEnum.UpdateRuleUpdate,
  UserActionTypeEnum.UpdateRuleDelete,
  UserActionTypeEnum.UpdateRuleOverrideSet,
  UserActionTypeEnum.UpdateRuleOverrideDelete,
  UserActionTypeEnum.PosFileUpload,
  UserActionTypeEnum.PosFileDelete,
  UserActionTypeEnum.PosReboot,
  UserActionTypeEnum.PosRestartFront,
  UserActionTypeEnum.PosRestartComputer,
  UserActionTypeEnum.PosCreate,
  UserActionTypeEnum.PosUpdate,
  UserActionTypeEnum.PosDelete,
  UserActionTypeEnum.TerminalCreate,
  UserActionTypeEnum.TerminalUpdate,
  UserActionTypeEnum.TerminalDelete,
  // ApplicationSetting* сюда не входят: админ-настройки хаба есть на всех контурах.
];

export const KIOSK_USER_ACTION_ENTITY_TYPES: readonly UserActionEntityTypeEnum[] = [
  UserActionEntityTypeEnum.ExternalOrganization,
  UserActionEntityTypeEnum.Network,
  UserActionEntityTypeEnum.TargetVersion,
  UserActionEntityTypeEnum.License,
  UserActionEntityTypeEnum.KioskContent,
  UserActionEntityTypeEnum.KioskVersion,
  UserActionEntityTypeEnum.KioskConfig,
  UserActionEntityTypeEnum.Credentials,
  UserActionEntityTypeEnum.KioskMaintenance,
  UserActionEntityTypeEnum.UpdateRule,
  UserActionEntityTypeEnum.PosFile,
  UserActionEntityTypeEnum.Pos,
  UserActionEntityTypeEnum.Terminal,
];

/**
 * Варианты фильтров истории действий для текущего контура.
 * На контуре ГеКо (production: ru.ge-co.io, ME/EU) сущностей экосистемы киосков не бывает,
 * поэтому их значения в фильтрах не показываем. На киосковом контуре и на staging — весь список.
 * Первым элементом идёт null — пункт «без фильтра».
 */
export function getUserActionFilterOptions(publishedFor: PublishedFor): {
  entityTypes: string[];
  actionTypes: string[];
} {
  const withoutKioskTypes = publishedFor === 'production';

  const entityTypes = Object.keys(UserActionEntityTypeEnum).filter(
    (key) => !withoutKioskTypes || !KIOSK_USER_ACTION_ENTITY_TYPES.includes(key as UserActionEntityTypeEnum),
  );
  const actionTypes = Object.keys(UserActionTypeEnum).filter(
    (key) => !withoutKioskTypes || !KIOSK_USER_ACTION_TYPES.includes(key as UserActionTypeEnum),
  );

  return {
    entityTypes: [null, ...entityTypes],
    actionTypes: [null, ...actionTypes],
  };
}
